import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Box,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import NumberFormat from "react-number-format";
import InputBase from "@mui/material/InputBase";

import CustomInput from "../common/customInput";
import ModalLoading from "../common/modalLoading";
import colors from "../../../config/colors";
import * as apiService from "../../../api";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
  maxWidth: "800px",
  bgcolor: "background.paper",
  border: `2px solid ${colors.primary}`,
  boxShadow: 24,
  p: 4,
  maxHeight: "90vh",
  overflow: "auto",
};

const labelStyles = {
  fontFamily: "Montserrat",
  fontWeight: "bold",
  fontSize: "14px",
  color: colors.primary,
  marginBottom: "8px",
};

const inputStyles = {
  boxShadow: "0 3px 10px rgb(0 0 0 / 0.2)",
  borderRadius: "10px",
  backgroundColor: "white",
  fontFamily: "Montserrat",
  padding: "12px 16px",
  fontSize: "14px",
  border: `1px solid ${colors.primary}`,
  width: "100%",
};

const steps = ["Teléfonos", "Dirección", "Correo Electrónico"];

// Estilos personalizados para el Stepper
const stepperStyles = {
  "& .MuiStepIcon-root": {
    color: "#e0e0e0", // Color para steps inactivos
    "&.Mui-active": {
      color: colors.primary, // Color primario para step activo
    },
    "&.Mui-completed": {
      color: colors.primary, // Color primario para steps completados
    },
  },
  "& .MuiStepIcon-text": {
    fill: "white", // Texto blanco dentro de las burbujas
  },
  "& .MuiStepConnector-alternativeLabel": {
    top: 10,
    left: "calc(-50% + 16px)",
    right: "calc(50% + 16px)",
  },
  "& .MuiStepConnector-alternativeLabel.Mui-active .MuiStepConnector-line": {
    borderColor: colors.primary,
  },
  "& .MuiStepConnector-alternativeLabel.Mui-completed .MuiStepConnector-line": {
    borderColor: colors.primary,
  },
};

const OnboardingModal = ({ open, onClose }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [provincias, setProvincias] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [municipiosFiltrados, setMunicipiosFiltrados] = useState([]);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const infoAfiliado = useSelector((state) => state.afiliado.infoAfiliado);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ mode: "onSubmit" });

  // Watch para cambios en provincia
  const watchProvincia = watch("idProvincia");

  const mostrarMensaje = useCallback(
    (mensaje, tipo) => {
      enqueueSnackbar(mensaje, {
        variant: tipo,
        action: (key) => (
          <Button
            style={{ color: "white", fontWeight: "bold" }}
            onClick={() => closeSnackbar(key)}
          >
            X
          </Button>
        ),
      });
    },
    [enqueueSnackbar, closeSnackbar]
  );

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const actualizarTelefonos = async (data) => {
    try {
      const telefonos = {
        TelefonoCelular: data.telefonoCelular,
        TelefonoResidencial: data.telefonoResidencial,
      };

      try {
        await apiService.afiliado.postUpdateTelefonos(
          infoAfiliado.codigoAfiliado,
          telefonos
        );
      } catch (apiError) {
        console.warn(
          "⚠️ Backend no disponible, simulando actualización de teléfonos:",
          apiError.message
        );
      }

      mostrarMensaje("Teléfonos actualizados exitosamente", "success");
      return true;
    } catch (error) {
      console.error("Error actualizando teléfonos:", error);
      mostrarMensaje(
        "Error actualizando teléfonos. Inténtelo nuevamente.",
        "error"
      );
      return false;
    }
  };

  const actualizarDireccion = async (data) => {
    try {
      const direccion = {
        Provincia:
          provincias.find((p) => p.codigo === Number(data.idProvincia))
            ?.descripcion || "",
        IdProvincia: Number(data.idProvincia),
        Municipio:
          municipios.find((m) => m.codigo === Number(data.idMunicipio))
            ?.descripcion || "",
        IdMunicipio: Number(data.idMunicipio),
        Calle: data.calle,
        NoCasa: data.noCasa,
        Apartamento: data.apartamento,
        Edificio: data.edificio,
        Sector: data.sector,
      };

      try {
        await apiService.afiliado.postUpdateDireccion(
          infoAfiliado.codigoAfiliado,
          direccion
        );
      } catch (apiError) {
        console.warn(
          "⚠️ Backend no disponible, simulando actualización de dirección:",
          apiError.message
        );
      }

      mostrarMensaje("Dirección actualizada exitosamente", "success");
      return true;
    } catch (error) {
      console.error("Error actualizando dirección:", error);
      mostrarMensaje(
        "Error actualizando dirección. Inténtelo nuevamente.",
        "error"
      );
      return false;
    }
  };

  const actualizarCorreo = async (data) => {
    try {
      const correo = {
        CorreoElectronico: data.correoElectronico,
      };

      try {
        const token = localStorage.getItem("token");
        await apiService.afiliado.postUpdateCorreo(
          infoAfiliado.codigoAfiliado,
          correo,
          token
        );
      } catch (apiError) {
        console.warn(
          "⚠️ Backend no disponible, simulando actualización de correo:",
          apiError.message
        );
      }

      mostrarMensaje("Correo electrónico actualizado exitosamente", "success");
      return true;
    } catch (error) {
      console.error("Error actualizando correo:", error);
      mostrarMensaje(
        "Error actualizando correo electrónico. Inténtelo nuevamente.",
        "error"
      );
      return false;
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      let exito = true;

      if (activeStep === 0) {
        // Actualizar teléfonos
        exito = await actualizarTelefonos(data);
      } else if (activeStep === 1) {
        // Actualizar dirección
        if (!data.idProvincia || !data.idMunicipio) {
          mostrarMensaje(
            "Por favor complete todos los campos obligatorios de ubicación.",
            "warning"
          );
          setLoading(false);
          return;
        }
        exito = await actualizarDireccion(data);
      } else if (activeStep === 2) {
        // Actualizar correo
        if (!data.correoElectronico) {
          mostrarMensaje(
            "Por favor ingrese un correo electrónico válido.",
            "warning"
          );
          setLoading(false);
          return;
        }
        exito = await actualizarCorreo(data);
      }

      if (exito) {
        if (activeStep === steps.length - 1) {
          // Último paso, cerrar modal
          mostrarMensaje(
            "¡Datos de contacto actualizados exitosamente!",
            "success"
          );
          setTimeout(() => {
            onClose();
          }, 1000);
        } else {
          // Ir al siguiente paso
          handleNext();
        }
      }
    } catch (error) {
      console.error("Error en onSubmit:", error);
      mostrarMensaje(
        "Ocurrió un error inesperado. Inténtelo nuevamente.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const cargarDatosIniciales = useCallback(async () => {
    setLoading(true);
    try {
      let datos, provinciasData, municipiosData;

      try {
        // Obtener datos de contacto
        const response = await apiService.afiliado.getDatosContactos(
          infoAfiliado.codigoAfiliado
        );
        datos = response.data;

        // Obtener dropdowns
        const [provinciasResponse, municipiosResponse] = await Promise.all([
          apiService.drops.getAllProvincias(),
          apiService.drops.getAllMunicipios(),
        ]);

        provinciasData = provinciasResponse.data.result;
        municipiosData = municipiosResponse.data.result;

        console.log("📊 Datos obtenidos del backend:", {
          datos,
          provincias: provinciasData.length,
          municipios: municipiosData.length,
        });
      } catch (apiError) {
        console.warn(
          "⚠️ Backend no disponible, usando datos simulados:",
          apiError.message
        );

        // Datos simulados para el afiliado
        datos = {
          telefonoCelular: "",
          telefonoResidencial: "",
          correoElectronico: "",
          idProvincia: "",
          idMunicipio: "",
          calle: "",
          noCasa: "",
          apartamento: "",
          edificio: "",
          sector: "",
        };

        // Datos simulados para dropdowns
        provinciasData = [
          { codigo: 1, descripcion: "Distrito Nacional" },
          { codigo: 2, descripcion: "Santo Domingo Este" },
          { codigo: 3, descripcion: "Santo Domingo Norte" },
          { codigo: 4, descripcion: "Santiago" },
        ];

        municipiosData = [
          { codigo: 1, codigoPadre: 1, descripcion: "Distrito Nacional" },
          { codigo: 2, codigoPadre: 2, descripcion: "Boca Chica" },
          { codigo: 3, codigoPadre: 3, descripcion: "Villa Altagracia" },
          {
            codigo: 4,
            codigoPadre: 4,
            descripcion: "Santiago de los Caballeros",
          },
        ];

        console.log("🧪 Usando datos simulados para testing");
      }

      setProvincias(provinciasData);
      setMunicipios(municipiosData);

      // Precargar datos en el formulario
      setValue("telefonoCelular", datos.telefonoCelular || "");
      setValue("telefonoResidencial", datos.telefonoResidencial || "");
      setValue("correoElectronico", datos.correoElectronico || "");
      setValue("idProvincia", datos.idProvincia || "");
      setValue("idMunicipio", datos.idMunicipio || "");
      setValue("calle", datos.calle || "");
      setValue("noCasa", datos.noCasa || "");
      setValue("apartamento", datos.apartamento || "");
      setValue("edificio", datos.edificio || "");
      setValue("sector", datos.sector || "");

      // Si ya tiene provincia seleccionada, filtrar municipios inmediatamente
      if (datos.idProvincia && municipiosData.length > 0) {
        console.log("🔍 Filtrando municipios para provincia:", {
          provinciaId: datos.idProvincia,
          tipoProvinciaId: typeof datos.idProvincia,
          municipiosTotales: municipiosData.length,
        });

        const municipiosFiltrados = municipiosData.filter((m) => {
          const codigoPadreNumerico = Number(m.codigoPadre);
          const provinciaNumerico = Number(datos.idProvincia);
          const coincide = codigoPadreNumerico === provinciaNumerico;

          console.log("🔍 Comparando municipio:", {
            municipio: m.descripcion,
            codigoPadre: m.codigoPadre,
            codigoPadreNumerico,
            provinciaNumerico,
            coincide,
          });

          return coincide;
        });

        console.log(
          "✅ Municipios filtrados encontrados:",
          municipiosFiltrados.length
        );
        setMunicipiosFiltrados(municipiosFiltrados);

        if (municipiosFiltrados.length === 0) {
          console.warn(
            "⚠️ PROBLEMA: No se encontraron municipios para la provincia inicial:",
            {
              provinciaId: datos.idProvincia,
              tipoProvinciId: typeof datos.idProvincia,
              municipiosTotales: municipiosData.length,
              ejemplosMunicipios: municipiosData.slice(0, 3).map((m) => ({
                descripcion: m.descripcion,
                codigoPadre: m.codigoPadre,
                tipoCodigoPadre: typeof m.codigoPadre,
              })),
            }
          );
        }
      }
    } catch (error) {
      console.error("Error cargando datos iniciales:", error);
      mostrarMensaje(
        "Error cargando información. Inténtelo nuevamente.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [infoAfiliado.codigoAfiliado, setValue, mostrarMensaje]);

  useEffect(() => {
    if (open && infoAfiliado.codigoAfiliado) {
      cargarDatosIniciales();
    }
  }, [open, infoAfiliado.codigoAfiliado, cargarDatosIniciales]);

  useEffect(() => {
    if (watchProvincia) {
      const municipiosFiltrados = municipios.filter(
        (m) => m.codigoPadre === Number(watchProvincia)
      );
      setMunicipiosFiltrados(municipiosFiltrados);
      setValue("idMunicipio", "");
    }
  }, [watchProvincia, municipios, setValue]);

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Actualice sus números de teléfono
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Mantenga actualizados sus números de contacto para recibir
                notificaciones importantes.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <label style={labelStyles}>Teléfono Celular</label>
              <NumberFormat
                format="(###)-###-####"
                mask="_"
                style={inputStyles}
                placeholder="(809)-555-5555"
                {...register("telefonoCelular")}
                onValueChange={(values) => {
                  setValue("telefonoCelular", values.value);
                }}
                customInput={InputBase}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <label style={labelStyles}>Teléfono Residencial</label>
              <NumberFormat
                format="(###)-###-####"
                mask="_"
                style={inputStyles}
                placeholder="(809)-555-5555"
                {...register("telefonoResidencial")}
                onValueChange={(values) => {
                  setValue("telefonoResidencial", values.value);
                }}
                customInput={InputBase}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Actualice su dirección
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Asegúrese de que su dirección esté actualizada para el envío de
                correspondencia.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel
                  style={{
                    color: colors.primary,
                    fontFamily: "Montserrat",
                    backgroundColor: "white",
                    padding: "0 4px",
                    marginLeft: "-4px",
                  }}
                  shrink={!!watch("idProvincia")}
                >
                  Provincia *
                </InputLabel>
                <Select
                  variant="outlined"
                  style={inputStyles}
                  {...register("idProvincia", {
                    required: "Debe seleccionar la provincia",
                  })}
                  value={watch("idProvincia") || ""}
                  onChange={(event) =>
                    setValue("idProvincia", event.target.value)
                  }
                >
                  {provincias.map((provincia) => (
                    <MenuItem key={provincia.codigo} value={provincia.codigo}>
                      {provincia.descripcion}
                    </MenuItem>
                  ))}
                </Select>
                {errors.idProvincia && (
                  <Typography color="error" variant="caption">
                    {errors.idProvincia.message}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel
                  style={{
                    color: colors.primary,
                    fontFamily: "Montserrat",
                    backgroundColor: "white",
                    padding: "0 4px",
                    marginLeft: "-4px",
                  }}
                  shrink={!!watch("idMunicipio")}
                >
                  Municipio *
                </InputLabel>
                <Select
                  variant="outlined"
                  style={inputStyles}
                  {...register("idMunicipio", {
                    required: "Debe seleccionar el municipio",
                  })}
                  value={watch("idMunicipio") || ""}
                  onChange={(event) =>
                    setValue("idMunicipio", event.target.value)
                  }
                  disabled={!watchProvincia}
                >
                  {municipiosFiltrados.map((municipio) => (
                    <MenuItem key={municipio.codigo} value={municipio.codigo}>
                      {municipio.descripcion}
                    </MenuItem>
                  ))}
                </Select>
                {errors.idMunicipio && (
                  <Typography color="error" variant="caption">
                    {errors.idMunicipio.message}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <label style={labelStyles}>Calle</label>
              <CustomInput
                style={inputStyles}
                placeholder="Nombre de la calle"
                {...register("calle")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <label style={labelStyles}>No. Casa</label>
              <CustomInput
                style={inputStyles}
                placeholder="Número de casa"
                {...register("noCasa")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <label style={labelStyles}>Apartamento</label>
              <CustomInput
                style={inputStyles}
                placeholder="Apartamento"
                {...register("apartamento")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <label style={labelStyles}>Edificio</label>
              <CustomInput
                style={inputStyles}
                placeholder="Edificio"
                {...register("edificio")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <label style={labelStyles}>Sector</label>
              <CustomInput
                style={inputStyles}
                placeholder="Sector"
                {...register("sector")}
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Actualice su correo electrónico
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Su correo electrónico es importante para recibir notificaciones
                y comunicaciones oficiales.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <label style={labelStyles}>Correo Electrónico *</label>
              <CustomInput
                style={inputStyles}
                placeholder="correo@ejemplo.com"
                type="email"
                {...register("correoElectronico", {
                  required: "El correo electrónico es obligatorio",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Ingrese un correo electrónico válido",
                  },
                })}
              />
              {errors.correoElectronico && (
                <Typography color="error" variant="caption">
                  {errors.correoElectronico.message}
                </Typography>
              )}
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  if (!open) return null;

  // Log de debugging
  console.log("🎭 OnboardingModal - Renderizando:", {
    open,
    activeStep,
    loading,
    codigoAfiliado: infoAfiliado.codigoAfiliado,
  });

  return (
    <>
      <Modal open={open} onClose={() => {}} disableEscapeKeyDown>
        <Box sx={style}>
          <Typography
            variant="h4"
            component="h2"
            style={{
              fontFamily: "Montserrat",
              fontWeight: "bold",
              textAlign: "center",
              color: colors.primary,
              marginBottom: "16px",
            }}
          >
            ¡Bienvenido a SENASA!
          </Typography>

          <Typography
            variant="body1"
            style={{
              fontFamily: "Montserrat",
              textAlign: "center",
              marginBottom: "24px",
              color: "#666",
            }}
          >
            Para brindarle el mejor servicio, necesitamos verificar y actualizar
            sus datos de contacto.
          </Typography>

          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{ mb: 4, ...stepperStyles }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {renderStepContent(activeStep)}

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  sx={{
                    fontFamily: "Montserrat",
                    fontWeight: "bold",
                    color: colors.primary,
                  }}
                >
                  Anterior
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    fontFamily: "Montserrat",
                    fontWeight: "bold",
                    backgroundColor: colors.primary,
                    "&:hover": {
                      backgroundColor: colors.primary,
                    },
                  }}
                >
                  {activeStep === steps.length - 1 ? "Finalizar" : "Siguiente"}
                </Button>
              </Box>
            </form>
          </Paper>
        </Box>
      </Modal>

      <ModalLoading open={loading} onCloseHandler={() => setLoading(false)} />
    </>
  );
};

export default OnboardingModal;
